import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CdkScrollable, ScrollDispatcher } from '@angular/cdk/scrolling';
import {
    AgentService,
    ApiService,
    BrandingModeService,
    FeatureService,
    hasNetworkExtension,
    LoggerService,
    MenuService,
    NETWORK_EXTENSIONS,
    NetworkService,
} from '@netfoundry-ui/shared/services';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import {
    FrontdoorExecutionActivityService,
    FrontdoorExtensionAvailabilityService,
    FrontdoorSelectionService,
    FRONTDOOR_SHARE_PAGES,
} from '@netfoundry-ui/shared/frontdoor-selection';
import { Environment, ENVIRONMENT, Network, NetworkV2, Tenant } from '@netfoundry-ui/shared/model';
import { take } from 'rxjs/operators';
import { NETWORK_SERVICE, NetworkServiceV2 } from '@netfoundry-ui/shared/apiv2';
import { NetworkUpgradeService } from '@netfoundry-ui/feature-network-upgrade';
import { NavigationEnd, Router } from '@angular/router';
import { URLS, ZITI_URLS } from '@netfoundry-ui/shared/services';
import { AccountService, BillingEnabledService } from '@netfoundry-ui/feature/shared-services';
import { Subscription } from 'rxjs';
import { invoke, debounce, defer, delay } from 'lodash';
import { SideNavigatorService } from './side-navigator.service';

@Component({
    selector: 'app-side-navigator',
    templateUrl: './side-navigator.component.html',
    styleUrls: ['./side-navigator.component.scss'],
    standalone: false,
})
export class SideNavigatorComponent implements OnInit, OnDestroy {
    @Input() disableNavigation = false;
    @Input() showExpandCollapseIcon = false;
    @Output() showExpandCollapseIconChange: EventEmitter<boolean> = new EventEmitter();

    currentRealm: any = {};
    realms: any[] = [];
    realmsMap: any = {};
    dialogRef: any;
    isOpened = false;
    filterString = '';
    hasAdd = true;
    url = '';
    billingStatusInvalid = false;
    latencyUrl = URLS.FABRIC_LATENCY;
    ctlLatencyUrl = URLS.CONTROLLER_LATENCY;
    scimUrl = URLS.SCIM;
    cloudzitiBillingURL = URLS.ORG_PAYMENT_PROFILE;
    networkControllerURL = URLS.NETWORK_CONTROLLERS;
    disabledToolTip = 'Network must be provisioned to access this page';
    agentDisabledToolTip = 'You must register an agent first before access.';
    Organizations = URLS.ORGANIZATIONS;
    /** Pulses the FD Executions icon. Read from a field, so only one subscription serves every item. */
    fdExecutionRunning = false;

    /**
     * The FD share extensions on the selected frontdoor's network, by extension key. Each is false until something has
     * answered, so a menu entry never vanishes.
     */
    fdExtensionEnabled: Record<string, boolean> = {};
    subscription = new Subscription();
    enterpriseBilling = false;
    hideBilling = false;
    tcpSharesEnabled = false;
    ipsecSharesEnabled = false;
    version;
    isZrok2 = false;
    hideMenuNavBar = true && this.featureService.isCloudZiti;
    currentNetwork: any = {};
    currentZitiEdgeDetails: any = {};
    appNameMap = {
        infrastructure: 'Network',
        networks: 'Network',
        billing: 'Billing',
        organization: 'Organization',
        support: 'Support',
        support_operations: 'Support_Operations',
        frontdoor: 'Frontdoor',
        on_prem: 'On_Prem',
    };
    appName = '';
    majorVersionNumber = '';
    minorVersionNumber = '';
    patchVersionNumber = '';
    onMenuScrollDebounced = debounce(this.onMenuScroll.bind(this), 100, { leading: true, maxWait: 100 });
    hideMenuScrollbarDebounced = debounce(this.hideMenuScrollbar.bind(this), 1000);
    constructor(
        public svc: SideNavigatorService,
        private logger: LoggerService,
        private apiService: ApiService,
        public authorizationService: AuthorizationService,
        @Inject(ENVIRONMENT) public environment: Environment,
        private networkUpgradeService: NetworkUpgradeService,
        public menuService: MenuService,
        public featureService: FeatureService,
        public agentService: AgentService,
        public brandingService: BrandingModeService,
        public brandingModeService: BrandingModeService,
        public billingEnabledService: BillingEnabledService,
        public accountService: AccountService,
        private router: Router,
        private scrollDispatcher: ScrollDispatcher,
        private cd: ChangeDetectorRef,
        // Appended rather than grouped with the other services: the spec builds this component
        // positionally, and inserting anywhere above would silently re-bind every argument.
        private frontdoorSelection: FrontdoorSelectionService,
        // Appended for the same reason as frontdoorSelection above.
        public frontdoorExecutionActivity: FrontdoorExecutionActivityService,
        private frontdoorExtensionAvailability: FrontdoorExtensionAvailabilityService
    ) {
        this.scrollDispatcher.scrolled().subscribe((event: any) => {
            this.onMenuScroll(event);
        });
    }

    onMenuScroll(event: any) {
        this.hideMenuNavBar = false;
        defer(() => {
            this.cd.detectChanges();
        });
        this.hideMenuScrollbarDebounced();
    }

    hideMenuScrollbar() {
        this.hideMenuNavBar = true && this.featureService.isCloudZiti;
        defer(() => {
            this.cd.detectChanges();
        });
    }

    showNavItem(item: any) {
        const showItem =
            (item.czV8Only && this.featureService.isCloudZiti === true) ||
            (item.mopV7Only && !(this.featureService.isCloudZiti === true));
        if (item.label === 'Organizational Settings' && showItem) {
            console.log(item);
        }
        return showItem;
    }

    get hideNav() {
        return !this.featureService.isCloudZiti && this.featureService.disableNewNavigation;
    }

    get showOrgPicker() {
        return (
            this.svc.area === 'Organization' &&
            (!this.billingEnabledService.billingEnabled || this.svc.tenants.length > 1) &&
            this.authorizationService.canListTenants() &&
            (!this.svc.isSelfService || this.authorizationService.isSupportUser)
        );
    }

    get showCustomerPicker() {
        return (
            this.svc.area === 'Customers' &&
            this.authorizationService.canListCustomers() &&
            this.svc.customers.length > 0
        );
    }

    get hideTitle() {
        return (
            (this.svc.area === 'Organization' && this.showOrgPicker) ||
            !this.svc.currentNav.title ||
            (this.svc.area === 'Customers' && this.showCustomerPicker)
        );
    }

    get showAppNameTitle() {
        return this.environment.v3Enabled;
    }

    /**
     * Whether this item's icon pulses. Only FD Executions does, and only while its own sub-app
     * reports work in flight — the network app has a Processes item on a different route.
     */
    isMenuItemRunning(item: any) {
        return this.fdExecutionRunning && item?.route === URLS.FD_PROCESSES;
    }

    /** True when every entry of an FD group is hidden, so its label does not stand alone. Only the FD navigator needs it. */
    hideFdGroup(group: any): boolean {
        return this.svc.currentNav?.app === 'fd' && group.menuItems.every((item: any) => this.hideMenuItem(item));
    }

    hideMenuItem(item: any) {
        if (item.experimental && !this.featureService.experimentalFeatures) {
            return true;
        }
        // supportOnly entries point at support tooling. The route guards them too,
        // so this only keeps the link out of sight for everyone else.
        if (item.supportOnly && !this.authorizationService.isSupportUser()) {
            return true;
        }
        let hide = false;
        const nameItemVal = item.route || item.action;
        switch (nameItemVal) {
            case 'openTour':
            case URLS.GETTING_STARTED:
                hide = !this.authorizationService.canCreateNetworks();
                break;
            case URLS.STORED_SECRETS:
                hide = !this.authorizationService.canListStoredSecret();
                break;
            case URLS.OIDC_AUDIENCES:
                hide = !this.featureService.experimentalFeatures;
                break;
            case URLS.FRONTDOOR_CLIENT_CERTIFICATES:
                hide = !this.tcpSharesEnabled;
                break;
            case URLS.FRONTDOOR_IPSEC_TUNNELS:
                hide = !this.ipsecSharesEnabled;
                break;
            // The FD sub-app's share sections, on the v3 API. Gated on the network extension rather
            // than the account flags above, which license the v1/v2 surfaces.
            case URLS.FD_HTTP_SHARES:
                hide = !this.fdExtensionEnabled[NETWORK_EXTENSIONS.HTTP_SHARES];
                break;
            case URLS.FD_TCP_SHARES:
                hide = !this.fdExtensionEnabled[NETWORK_EXTENSIONS.TCP_SHARES];
                break;
            case URLS.FD_IPSEC:
                hide = !this.fdExtensionEnabled[NETWORK_EXTENSIONS.IPSEC_TUNNELER];
                break;
            // Read live, because the grant is per frontdoor and the selection can change under the menu.
            case URLS.FD_AUDIT_EVENTS:
                hide =
                    !this.frontdoorSelection?.currentId ||
                    !this.authorizationService.canListFrontdoorAuditEvents(this.frontdoorSelection.currentId);
                break;
            // The entry opens Management; FdManagementGuard moves a caller with only ingress read to HTTP Ingress.
            case URLS.FD_OPERATIONS_MANAGEMENT: {
                const frontdoor = this.frontdoorSelection?.current;
                hide =
                    !frontdoor?.id ||
                    !(
                        this.authorizationService.canListFrontdoorIngresses(frontdoor.id) ||
                        this.authorizationService.canManageFrontdoorNetworkExtensions(frontdoor)
                    );
                break;
            }
            case URLS.IPSEC_TUNNELERS:
                // Per-network opt-in via NetworkExtension('IPSEC_TUNNELER'). Server populates
                // currentNetwork.extensions when getNetwork was fetched with embed=extensions
                // (NetworkServiceV2.getNetwork does this unconditionally). hasNetworkExtension
                // reads an unloaded extensions array as off, so the entry stays hidden until it
                // resolves rather than flickering in.
                hide = !hasNetworkExtension(this.currentNetwork, NETWORK_EXTENSIONS.IPSEC_TUNNELER);
                break;
            case URLS.FRONTDOOR_FRONTENDS:
                hide = this.isZrok2;
                break;
            case URLS.FRONTDOOR_NAMESPACES:
                hide = !this.isZrok2;
                break;
            case URLS.FRONTDOOR_HTTP:
                hide = !this.isZrok2;
                break;
            case URLS.FRONTDOOR:
                hide = this.isZrok2;
                break;
            // TCP and IPsec shares are v2-only and separately licensed, by the same account flags
            // list-header reads as isTCPenabled and isIPsecEnabled.
            case URLS.FRONTDOOR_TCP:
                hide = !this.isZrok2 || !this.tcpSharesEnabled;
                break;
            case URLS.FRONTDOOR_IPSEC_SHARES:
                hide = !this.isZrok2 || !this.ipsecSharesEnabled;
                break;
            case 'openTicket':
                hide = this.environment.hideSubmitTicket;
                break;
            case URLS.BILLING:
            case URLS.PAYMENT_PROFILE:
                if (this.hideBilling) {
                    hide = true;
                } else if (this.appName === 'support') {
                    hide = this.authorizationService.canListUserIdentities();
                }
                break;
            case URLS.ORG_SETTINGS:
            case URLS.SETTINGS:
                hide =
                    (item.czV8Only && !this.featureService.isCloudZiti) ||
                    (item.mopV7Only && this.featureService.isCloudZiti);
                break;
            default:
                hide = false;
                break;
        }
        return hide;
    }

    ngOnInit(): void {
        if (this.menuService.locked) {
            const area = localStorage.getItem('NavArea');
            if (area != null) {
                this.menuService.setArea(area);
            } else {
                this.menuService.setArea('Network');
            }
        }
        this.subscription.add(
            this.router.events.subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this.url = event.url.split('?')[0];
                }
            })
        );
        this.subscription.add(
            this.accountService.currentAccount.subscribe((account) => {
                this.enterpriseBilling = account?.billingMode === 'Enterprise';
            })
        );
        // The service only polls while the FD sub-app is open, so this stays false everywhere else.
        this.subscription.add(
            this.frontdoorExecutionActivity.running$.subscribe((running) => {
                this.fdExecutionRunning = running;
            })
        );
        // Follows the frontdoor selection rather than the console's selected network, because the FD
        // sub-app is scoped to a frontdoor that names its own network.
        FRONTDOOR_SHARE_PAGES.forEach(({ extension }) =>
            this.subscription.add(
                this.frontdoorExtensionAvailability.enabled$(extension).subscribe((enabled) => {
                    this.fdExtensionEnabled = { ...this.fdExtensionEnabled, [extension]: enabled };
                })
            )
        );
        this.subscription.add(
            this.apiService.currentContract.subscribe((contract) => {
                this.hideBilling = contract?.productId === 'NetfoundyTrialPlan';
            })
        );
        this.subscription.add(
            this.apiService.currentTenant.subscribe((tenant) => {
                this.agentService.getAccountById(tenant.id).subscribe((data: any) => {
                    this.tcpSharesEnabled = data.tcpSharesEnabled;
                    this.ipsecSharesEnabled = data.ipsecSharesEnabled;
                    this.version = data.version;
                    if (data.version > 1) {
                        this.isZrok2 = true;
                    } else {
                        this.isZrok2 = false;
                    }
                });
            })
        );
        this.subscription.add(
            this.apiService.currentRealm.subscribe((realm) => {
                this.currentRealm = realm;
            })
        );
        this.subscription.add(
            this.apiService.availableRealms.subscribe((realms) => {
                this.realms = realms || [];
                this.realmsMap = {};
                for (const realm of this.realms) {
                    this.realmsMap[realm.id] = realm;
                }
            })
        );
        this.subscription.add(
            this.apiService.currentNetwork.subscribe((network) => {
                this.currentNetwork = network;
                if (this.currentNetwork?.id) {
                    this.majorVersionNumber = this.apiService.getNetworkVersion(this.currentNetwork);
                    this.minorVersionNumber = this.apiService.getNetworkMinorVersion(this.currentNetwork);
                    this.patchVersionNumber = this.apiService.getNetworkPatchVersion(this.currentNetwork);
                }
                this.hasAdd = this.authorizationService.canCreateNetworks();
            })
        );
        this.subscription.add(
            this.menuService.appChanged.subscribe((appName) => {
                this.appName = appName;
                /* The sidebar needs the frontdoor selection before any FD page has mounted, because it gates
                 * its IPsec entry on that frontdoor's network. Every FD page loads the selection in its own
                 * ngOnInit, so without this the sidebar is a step behind. */
                if (appName === 'fd') {
                    this.frontdoorSelection.load();
                }
            })
        );
        this.subscription.add(
            this.router.events.subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this.svc.updateSelectedNavItem();
                }
            })
        );
        this.subscription.add(
            this.apiService.currentZitiEdgeDetails.subscribe((details) => {
                this.currentZitiEdgeDetails = details;
            })
        );
    }

    navItemSelected(item: any) {
        // Highlight Services while on the Templates page (Templates now lives as a tab of the
        // Services area). This is a URL check rather than adding /service-templates to the
        // Services item's selectedRoutes: that would make Services the active nav item, and
        // the resulting change-detection disrupts the Templates list's ag-grid column sizing.
        // The .selected class is paint-only, so applying it here has no such side effect.
        if (
            item.route === ZITI_URLS.ZITI_SERVICES &&
            [ZITI_URLS.ZITI_SERVICE_TEMPLATES, ZITI_URLS.ZITI_SERVICE_TEMPLATES_LIST].includes(
                this.router.url.split('?')[0].split(';')[0]
            )
        ) {
            return true;
        }
        return item.selectedRoutes?.includes(this.svc.selectedNavItem.route);
    }

    doNavAction(item: any) {
        if (this.disableNavigation || this.svc.isNavItemDisabled(item)) {
            return;
        }
        // The consolidated Dashboard/Identities items resolve their destination from the beta toggle at click time.
        let route = item.route;
        if (item.dashboardSwitch) {
            route = this.featureService.dashboardRoute;
        } else if (item.identitiesSwitch) {
            route = this.featureService.identitiesRoute;
        } else if (item.betaListSwitch) {
            route = this.featureService.betaListRoute(item.route);
        }
        if (route) {
            this.svc.navigateToRoute({ ...item, route });
        } else if (item.link) {
            window.open(item.link, '_blank');
        } else if (item.action) {
            invoke(this.svc, item.action);
        }
    }

    toggleList(event: any) {
        event.stopPropagation();
        this.isOpened = !this.isOpened;
    }

    stopProp(event: any) {}

    public setNetwork(network: any) {
        this.apiService.setCurrentNetwork(network);
        this.closeList();
    }

    public setTenant(tenant: any) {
        this.apiService.setCurrentTenant(tenant);
        this.closeList();
    }

    public setOrganization(organization: any) {
        this.apiService.setCurrentOrganization(organization);
        this.closeList();
    }

    public setCustomer(customer: any) {
        this.apiService.setCurrentCustomer(customer);
        this.closeList();
    }

    public setShare(frontdoor: any) {
        this.apiService.setCurrentShare(frontdoor);
        this.closeList();
    }

    organizationSelected(organization: any) {
        return this.svc?.currentOrganization?.id === organization?.id;
    }

    getRealmPrefix(organization: any) {
        if (this.currentRealm.label !== 'netfoundry' || !this.authorizationService.isSupportUser()) {
            return '';
        }
        if (organization?.realmId === this.currentRealm?.id) {
            return '';
        }
        const realm = this.realmsMap[organization.realmId];
        const realmLabel = realm?.label;
        if (!realmLabel) {
            return '';
        }
        return realmLabel + ' / ';
    }

    toggleLock() {
        this.menuService.toggleLock();
    }

    closeList() {
        this.filterString = '';
        this.isOpened = false;
    }

    showExpandCollapse(event: any) {
        defer(() => {
            this.showExpandCollapseIcon = true;
            this.showExpandCollapseIconChange.emit(this.showExpandCollapseIcon);
        });
    }

    hideExpandCollapse(event: any) {
        this.showExpandCollapseIcon = false;
        this.showExpandCollapseIconChange.emit(this.showExpandCollapseIcon);
    }

    collapseNav(event: any) {
        if (this.svc.area.length > 0) {
            this.menuService.unsetArea();
        } else {
            this.menuService.resetArea();
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    get expandCollapseIconVisible() {
        return this.showExpandCollapseIcon || this.svc?.area?.length <= 0;
    }
}
